import { useState, useEffect } from 'react';
import {
  ClickOutsideDetector,
  IconExpand,
  IconUp,
  IconDown,
  Rangepicker,
  Checkbox,
  InputText,
  formatDate,
  ButtonPrimary,
  ButtonPseudo,
  ButtonSize,
  IconClose,
  InputSize,
  SelectItemProps,
  IconLoader,
} from '@ui/src';
import { WarningsReportFilterTypes, WarningsReportFilterValues } from './types';

import cn from './styles.module.css';

let searchTimeout: NodeJS.Timeout;

const WarningsReportFilter = ({
  filters,
  setFilters,
  name,
  title,
  type,
  list,
  onSearch,
  selectedValues,
  setSelectedValues,
  listLoading,
  listSearchText,
  setListSearch,
  wrapperRef,
  onScroll,
  nextPageIsLoading,
  rightPlace,
}: WarningsReportFilterTypes) => {
  const [toggledOn, setToggledOn] = useState(false);
  const [values, setValues] = useState<string[]>([]);
  const [searchText, setSearchText] = useState('');
  const [selectedItems, setSelectedItems] = useState<SelectItemProps[]>([]);

  useEffect(() => {
    if (filters[name as keyof WarningsReportFilterValues] && type !== 'date') {
      setValues(filters[name as keyof WarningsReportFilterValues] as string[]);
    }
  }, [filters, toggledOn]);

  let content = <div />;
  let filterValue;

  if (type === 'date') {
    if (
      (filters[name as keyof WarningsReportFilterValues] as string[])
        ?.length === 1
    ) {
      filterValue = formatDate(
        (filters[name as keyof WarningsReportFilterValues] as string[])[0],
      );
    } else if (
      (filters[name as keyof WarningsReportFilterValues] as string[])?.length >
      1
    ) {
      filterValue = `${formatDate(
        (filters[name as keyof WarningsReportFilterValues] as string[])[0],
      )} - ${formatDate(
        (filters[name as keyof WarningsReportFilterValues] as string[])[1],
      )}`;
    }

    content = (
      <Rangepicker
        startDate={new Date(1970, 0, 1)}
        endDate={new Date(2099, 0, 1)}
        onApply={(date) => {
          const dateList: string[] = [];

          dateList.push(date[0]?.toISOString() as string);

          if (date?.length > 1) {
            dateList.push(date[1]?.toISOString() as string);
          }

          setFilters({
            ...filters,
            [name]: dateList,
          });

          setToggledOn(false);
        }}
        value={
          filters[name as keyof WarningsReportFilterValues]
            ? (filters[name as keyof WarningsReportFilterValues] as string[])
            : []
        }
        onReset={() => {
          setFilters({
            ...filters,
            [name]: undefined,
          });

          setToggledOn(false);
        }}
        twoPickers
        withoutCustomSelector
      />
    );
  } else if (type === 'checkbox' && list) {
    if (
      (filters[name as keyof WarningsReportFilterValues] as string[])?.length >
      1
    ) {
      filterValue = `${
        filters[name as keyof WarningsReportFilterValues]?.length
      } selected`;
    } else if (
      (filters[name as keyof WarningsReportFilterValues] as string[])
        ?.length === 1
    ) {
      filterValue = list?.find(
        (item) =>
          item.value ===
          (filters[name as keyof WarningsReportFilterValues] as string[])[0],
      )?.name;
    }

    content = (
      <div>
        {list?.map((item) => (
          <div className={cn.checkboxWrapper} key={item.value}>
            <Checkbox
              type="checkbox"
              name={`${filters[name as keyof WarningsReportFilterValues]}-${
                item.value
              }`}
              title={item.name}
              checked={values?.includes(item.value as string)}
              onChange={() => {
                let nextFilters = ([] as string[]).concat(values);

                if (values?.includes(item.value as string)) {
                  nextFilters = nextFilters?.filter(
                    (value) => value !== item.value,
                  );
                } else {
                  nextFilters.push(item.value as string);
                }

                setValues(nextFilters);
              }}
            />
          </div>
        ))}
        <div className={cn.filterFooter}>
          <ButtonPrimary
            size={ButtonSize.SMALL}
            text="Apply"
            onClick={() => {
              setToggledOn(false);
              setFilters({
                ...filters,
                [name]: values,
              });
            }}
            className={cn.applyButton}
          />
          <ButtonPseudo
            size={ButtonSize.SMALL}
            text="Clear"
            onClick={() => {
              setToggledOn(false);
              setFilters({
                ...filters,
                [name]: undefined,
              });
              setValues([]);
            }}
          />
        </div>
      </div>
    );
  } else if (type === 'select') {
    if (selectedValues?.length) {
      if (selectedValues?.length > 1) {
        filterValue = `${selectedValues?.length} selected`;
      } else if (selectedValues?.length === 1) {
        if (selectedValues[0]?.name) {
          filterValue = selectedValues[0]?.name;
        } else {
          filterValue = '1 selected';
        }
      }
    } else if (!selectedValues?.length && values?.length) {
      filterValue = `${values?.length} selected`;
    }

    content = (
      <div className={cn.selectFilter}>
        <div className={cn.inputWrapper}>
          <InputText
            size={InputSize.SMALL}
            name="searchText"
            value={searchText}
            title=""
            placeholder="Enter Name or Company Name"
            onChange={(e) => {
              setSearchText(e.target.value);
              clearTimeout(searchTimeout);

              if (onSearch) {
                searchTimeout = setTimeout(() => {
                  onSearch(e.target.value);
                }, 700);
              }
            }}
          />
        </div>
        <div className={cn.listContent} ref={wrapperRef} onScroll={onScroll}>
          {!listSearchText &&
            !listLoading &&
            selectedValues &&
            selectedValues?.length > 0 &&
            selectedValues
              ?.filter((item) => item.name)
              ?.map((item) => (
                <div
                  className={`${cn.checkboxWrapper} ${cn.flex}`}
                  key={item.value}
                >
                  <Checkbox
                    type="checkbox"
                    name={`${
                      filters[name as keyof WarningsReportFilterValues]
                    }-${item.value}`}
                    title={item.name}
                    checked={values?.includes(item.value as string)}
                    onChange={() => {
                      let nextFilters = ([] as string[]).concat(values);

                      let nextSelectedItems = ([] as SelectItemProps[]).concat(
                        selectedItems,
                      );

                      if (values?.includes(item.value as string)) {
                        nextFilters = nextFilters?.filter(
                          (value) => value !== item.value,
                        );

                        nextSelectedItems = nextSelectedItems?.filter(
                          (listItem) => listItem.value !== item.value,
                        );
                      } else {
                        nextFilters.push(item.value as string);
                      }

                      setValues(nextFilters);

                      const newSelectedItems = nextFilters?.filter(
                        (filterItem) =>
                          nextSelectedItems.every(
                            (listItem) => filterItem !== listItem.value,
                          ),
                      );

                      newSelectedItems?.forEach((filterItem) => {
                        nextSelectedItems.push({
                          name: list?.find(
                            (listItem) => listItem.value === filterItem,
                          )?.name as string,
                          value: filterItem,
                        });
                      });

                      setSelectedItems(nextSelectedItems);
                    }}
                  />
                </div>
              ))}
          {listLoading && !nextPageIsLoading && toggledOn && (
            <div className={cn.listLoading}>
              <IconLoader />
            </div>
          )}
          {list
            ?.filter((item) =>
              selectedValues && !searchText
                ? selectedValues.every(
                    (selected) => item.value !== selected.value,
                  )
                : item,
            )
            ?.filter((item) => item.name)
            ?.map((item) => (
              <div
                className={`${cn.checkboxWrapper} ${cn.flex}`}
                key={item.value}
              >
                <Checkbox
                  type="checkbox"
                  name={`${filters[name as keyof WarningsReportFilterValues]}-${
                    item.value
                  }`}
                  title={item.name}
                  checked={values?.includes(item.value as string)}
                  value={item.value as string}
                  onChange={() => {
                    let nextFilters = ([] as string[]).concat(values);

                    let nextSelectedItems = ([] as SelectItemProps[]).concat(
                      selectedItems,
                    );

                    if (values?.includes(item.value as string)) {
                      nextFilters = nextFilters?.filter(
                        (value) => value !== item.value,
                      );

                      nextSelectedItems = nextSelectedItems?.filter(
                        (listItem) => listItem.value !== item.value,
                      );
                    } else {
                      nextFilters.push(item.value as string);
                    }

                    setValues(nextFilters);

                    const newSelectedItems = nextFilters?.filter((filterItem) =>
                      nextSelectedItems.every(
                        (listItem) => filterItem !== listItem.value,
                      ),
                    );

                    newSelectedItems?.forEach((filterItem) => {
                      nextSelectedItems.push({
                        name: list?.find(
                          (listItem) => listItem.value === filterItem,
                        )?.name as string,
                        value: filterItem,
                      });
                    });

                    setSelectedItems(nextSelectedItems);
                  }}
                />
              </div>
            ))}
          {nextPageIsLoading && (
            <div className={cn.nextPageLoader}>
              <IconLoader width={25} height={25} />
            </div>
          )}
        </div>
        <div className={cn.filterFooter}>
          <ButtonPrimary
            size={ButtonSize.SMALL}
            text="Apply"
            onClick={() => {
              setToggledOn(false);
              setFilters({
                ...filters,
                [name]: values,
              });

              if (searchText) {
                setSearchText('');

                if (setListSearch) {
                  setListSearch('');
                }

                if (onSearch) {
                  onSearch('');
                }
              }

              if (setSelectedValues) {
                setSelectedValues(selectedItems);
              }
            }}
            className={cn.applyButton}
            disabled={listLoading}
          />
          <ButtonPseudo
            size={ButtonSize.SMALL}
            text="Clear"
            onClick={() => {
              setToggledOn(false);
              setFilters({
                ...filters,
                [name]: undefined,
              });
              setValues([]);

              if (setSelectedValues) {
                setSelectedValues([]);
              }

              setSearchText('');

              if (setListSearch) {
                setListSearch('');
              }

              if (onSearch) {
                onSearch('');
              }
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <ClickOutsideDetector
      setOptionsListVisible={() => {
        setToggledOn(false);
        setValues([]);

        if (type === 'select') {
          setSearchText('');

          if (onSearch && searchText && setListSearch) {
            onSearch('');

            setListSearch('');
          }
        }
      }}
    >
      <div className={cn.filterWrapper}>
        {!filters[name as keyof WarningsReportFilterValues]?.length && (
          <button
            type="button"
            className={`${cn.filterButton} ${
              filters[name as keyof WarningsReportFilterValues]
                ? cn.withValue
                : ''
            }`}
            onClick={() => {
              setToggledOn(!toggledOn);
              setValues([]);

              if (toggledOn) {
                if (type === 'select') {
                  setSearchText('');

                  if (onSearch && searchText && setListSearch) {
                    onSearch('');

                    setListSearch('');
                  }
                }
              }
            }}
          >
            <IconExpand width={17} height={17} />
            <div className={cn.filterButtonTitle}>{title}</div>
          </button>
        )}
        {filters[name as keyof WarningsReportFilterValues] &&
          (filters[name as keyof WarningsReportFilterValues] as string[])
            ?.length > 0 && (
            <div className={cn.filterValueWrapper}>
              <button
                type="button"
                className={cn.clearFilterButton}
                onClick={() => {
                  const nextFilters = {
                    ...filters,
                    [name]: undefined,
                  };

                  if (type === 'date') {
                    delete nextFilters.createdAtFrom;
                    delete nextFilters.createdAtTo;
                  }
                  setFilters(nextFilters);

                  setValues([]);

                  if (setSelectedValues) {
                    setSelectedValues([]);
                  }
                }}
              >
                <IconClose width={16} height={16} />
              </button>
              <div className={cn.filterValueTitle}>{`${title}:`}</div>
              <button
                type="button"
                className={cn.showFilterContentButton}
                onClick={() => {
                  setToggledOn(!toggledOn);
                  setValues([]);

                  if (toggledOn) {
                    if (type === 'select') {
                      setSearchText('');

                      if (onSearch && searchText && setListSearch) {
                        onSearch('');

                        setListSearch('');
                      }
                    }
                  }
                }}
              >
                {filterValue}
                {!toggledOn ? <IconDown /> : <IconUp />}
              </button>
            </div>
          )}
        {toggledOn && (
          <div
            className={`${cn.filterContent} ${rightPlace ? cn.rightPlace : ''}`}
          >
            {content}
          </div>
        )}
      </div>
    </ClickOutsideDetector>
  );
};

export default WarningsReportFilter;
